// Bounded stateful line accumulator. Feed Buffer/string chunks in, get
// complete lines out (split on "\n", tolerating a trailing "\r" i.e. CRLF).
//
// line_max_mib: a line exceeding this is NOT yielded as raw text — instead a
// synthetic oversized-line marker OBJECT is yielded directly (not a JSON
// string needing re-parse). stream-json-event-parser.ts is written to accept
// either a raw line string OR one of these marker objects from upstream, so
// downstream JSON.parse never chokes on a truncated/oversized payload.
//
// raw_log_max_mib: total across the whole session. Once exceeded, this
// module stops calling the caller-supplied `onRawByte` sink (comment below
// documents why), but keeps splitting/parsing logic running — i.e. still
// yields lines for downstream consumers. This module itself does not persist
// to disk; `onRawByte` is how a caller layers persistence on top, and the cap
// gates THAT callback, not the parsing/yielding path.
export interface OversizedLineMarker {
  kind: "oversized_line";
  truncatedPreview: string;
  originalLengthBytes: number;
}

export type SplitterYield = string | OversizedLineMarker;

export interface LineSplitterOptions {
  lineMaxMib: number;
  rawLogMaxMib: number;
  // Optional sink for raw bytes as they arrive, gated by the total cap. Once
  // the raw_log_max_mib cap is exceeded this stops being called — callers
  // that persist raw stream bytes to disk should wire this to their sink and
  // rely on the gating here instead of re-implementing the cap themselves.
  onRawByte?: (chunk: string) => void;
}

const MIB = 1024 * 1024;

export class BoundedLineSplitter {
  private readonly lineMaxBytes: number;
  private readonly rawLogMaxBytes: number;
  private buffer = "";
  private totalRawBytesSeen = 0;
  private rawSinkCapped = false;

  constructor(private readonly options: LineSplitterOptions) {
    this.lineMaxBytes = options.lineMaxMib * MIB;
    this.rawLogMaxBytes = options.rawLogMaxMib * MIB;
  }

  // Feed a chunk; returns any complete lines/markers it can now yield.
  feed(chunk: Buffer | string): SplitterYield[] {
    const text = typeof chunk === "string" ? chunk : chunk.toString("utf8");

    this.totalRawBytesSeen += Buffer.byteLength(text, "utf8");
    if (this.totalRawBytesSeen > this.rawLogMaxBytes) {
      this.rawSinkCapped = true;
    }
    if (!this.rawSinkCapped && this.options.onRawByte) {
      this.options.onRawByte(text);
    }

    this.buffer += text;
    const out: SplitterYield[] = [];
    let newlineIndex = this.buffer.indexOf("\n");
    while (newlineIndex !== -1) {
      let rawLine = this.buffer.slice(0, newlineIndex);
      if (rawLine.endsWith("\r")) rawLine = rawLine.slice(0, -1); // CRLF tolerance
      out.push(this.toYield(rawLine));
      this.buffer = this.buffer.slice(newlineIndex + 1);
      newlineIndex = this.buffer.indexOf("\n");
    }
    return out;
  }

  // End of stream: flush whatever partial line remains in the buffer (if
  // non-empty) as a final line/marker.
  flushRemaining(): SplitterYield[] {
    if (this.buffer.length === 0) return [];
    const rawLine = this.buffer;
    this.buffer = "";
    return [this.toYield(rawLine)];
  }

  private toYield(rawLine: string): SplitterYield {
    const lengthBytes = Buffer.byteLength(rawLine, "utf8");
    if (lengthBytes <= this.lineMaxBytes) return rawLine;
    return {
      kind: "oversized_line",
      truncatedPreview: rawLine.slice(0, 256),
      originalLengthBytes: lengthBytes,
    };
  }
}
